import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    Stack,
    Typography,
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import _ from 'lodash';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import setLoading from '../../component/linear-determinate';
import CommonInput from '../../component/form/common-input.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import { ReactComponent as DeleteForeverIcon } from '../../static/images/svg/ic_baseline-delete-forever.svg';
import Table from '../../component/table/table';
import { MUIDataTableColumnDef, MUIDataTableCustomHeadRenderer } from 'mui-datatables';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import SubFunctionProjectSettingSaveButton from './sub-function-project-setting-save-button.component';
import { Option } from '../../component/form/select-input-multiple';
import { RbpCloudSubFunctionAuxiliaryModeContext } from './rbp-cloud-sub-function-auxiliary-mode-context';
import { SubFunctionAuxiliaryHint } from './sub-function-auxiliary-hint.component';
import { DisSettingPageSkeleton } from './sub-function-settings-skeletons.component';
import { SelectOption } from '../../component/form/base-input.component';
import LSM from '../../modules/cache/tool-local-storage';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import BASE_CHECK_TYPE from '../../constants/regular.constant';
import DisRestaurantTaxDrawer, { DisRestaurantTaxFormValues } from './dis-restaurant-tax-drawer.component';
import DisGybDetailDrawer, { DisGybDetailRecord } from './dis-gyb-detail-drawer.component';
import './scss/dis-setting.page.scss';

type Props = Record<string, never>;

/** KDS 打印机项（与 RBP2 一致） */
type KdsPrinter = {
    /** 打印机设备 id */
    print_device_id?: number;
    /** 展示名称 */
    print_device_name?: string;
    /** 打印机 IP 或主机名 */
    printer_name?: string;
};

/** 发票基础设置中单行形态 */
type DisInvoiceRow =
    | {
          key: 43;
          type: 'radio';
          value: number;
          labelKey: string;
          options: { value: number; labelKey: string }[];
      }
    | {
          key: 10 | 33 | 18;
          type: 'text' | 'number';
          value: string;
          labelKey: string;
      }
    | {
          key: 162;
          type: 'switch';
          value: boolean;
          labelKey: string;
      };

type DialogType = {
    /** 是否展示确认框 */
    visible: boolean;
    /** 说明文案 */
    desc: string;
    /** 业务类型：sync | delete-rest | delete-rest-ids */
    type: string;
};

type State = {
    /** 全页保存中 */
    isSaving: boolean;
    /** 发票相关设置行 */
    invoiceRows: DisInvoiceRow[];
    /** 是否为西班牙门店 */
    isSpain: boolean;
    /** 餐厅国家名（与 RBP2 `this.country` 一致） */
    countryName: string;
    /** 打印机列表 */
    kdsPrinterList: KdsPrinter[];
    /** 已选测试打印机（可多选） */
    selectedKdsPrinterList: KdsPrinter[];
    /** 测试打印机参数 */
    testPrintParams: { type: string; agree: string; port: string }[];
    /** 重试小票打印次数 */
    printRetryNumber: number;
    /** 餐厅税务信息 */
    kdsRestInfoList: DisRestaurantTaxFormValues[];
    /** GYB 设备列表 */
    gybInvoiceInfoList: DisGybDetailRecord[];
    /** 餐厅税务弹窗（新增/编辑/西语只读，见 `DisRestaurantTaxDrawer` 内 `DialogComponent`） */
    taxDrawerOpen: boolean;
    /** 税务表单数据 */
    taxFormDetail: DisRestaurantTaxFormValues;
    /** 是否新增税务 */
    taxIsAdd: boolean;
    /** GYB 设备详情弹窗 */
    gybDrawerOpen: boolean;
    /** GYB 详情数据 */
    gybFormDetail: DisGybDetailRecord;
    /** 确认弹窗 */
    dialogType: DialogType;
    /** 批量删除的税务 id */
    deleteIds: number[];
    /** 全局增值税率（新增税务写死字段用） */
    taxPrimary: string;
    /** 首包拉取完成前展示骨架 */
    initialPageDataReady: boolean;
    /** 餐厅税务弹窗主按钮提交中（与 `LoadingOverlayButton` 同步） */
    taxSaveSubmitting: boolean;
};

/**
 * 构建初始发票设置行定义。
 * @returns 初始行模板
 */
function buildInitialInvoiceRows(): DisInvoiceRow[] {
    return [
        {
            key: 43,
            type: 'radio',
            value: 0,
            labelKey: 'dis_setting_page_invoice_processing_method',
            options: [
                { value: 0, labelKey: 'dis_setting_page_generate_xml' },
                { value: 1, labelKey: 'dis_setting_page_generate_gyb' },
                { value: 2, labelKey: 'dis_setting_page_generate_aruba' },
            ],
        },
        { key: 10, type: 'text', value: '', labelKey: 'dis_setting_page_xml_output_url' },
        { key: 33, type: 'number', value: '', labelKey: 'dis_setting_page_initial_invoice_number' },
        { key: 18, type: 'text', value: '', labelKey: 'dis_setting_page_electronic_invoice_number_suffix' },
        { key: 162, type: 'switch', value: false, labelKey: 'dis_setting_page_xml_auto_generate' },
    ];
}

/** 打印机类型下拉项 */
const PRINTER_TYPE_ITEMS: SelectOption[] = [{ value: 'EPSON', label: 'EPSON' }];
/** 连接协议下拉项 */
const PRINTER_AGREE_ITEMS: SelectOption[] = [{ value: 'tcp', label: 'TCP' }];

/**
 * 云 RBP「子功能管理」— DIS 设置：发票打印、发票基础项、餐厅税务、GYB 设备（对齐 Figma 1901:25387 与 RBP2 `project-setting-dis`）。
 * 隧道校验在子功能壳页 `HookDecorator` 中统一完成。
 */
class DisSettingPage extends React.Component<Props, State> {
    /** 顶栏「辅助模式」：发票设置沿用既有 `figma_aux_dis_*` 说明；餐厅税务 / GYB 用稿面区块说明 key */
    static contextType = RbpCloudSubFunctionAuxiliaryModeContext;
    /** 由 `contextType` 注入 */
    context!: React.ContextType<typeof RbpCloudSubFunctionAuxiliaryModeContext>;

    /** 防止保存重复提交 */
    private saveLocked = false;

    public constructor(props: Props) {
        super(props);
        const cachedRetry = LSM.get(LOCAL_STORAGE_KEYS.DIS_SETTING_PRINT_RETRY_NUM);
        this.state = {
            isSaving: false,
            invoiceRows: buildInitialInvoiceRows(),
            isSpain: false,
            countryName: '',
            kdsPrinterList: [],
            selectedKdsPrinterList: [],
            testPrintParams: [{ type: 'EPSON', agree: 'tcp', port: '9100' }],
            printRetryNumber: typeof cachedRetry === 'number' && cachedRetry > 0 ? cachedRetry : 3,
            kdsRestInfoList: [],
            gybInvoiceInfoList: [],
            taxDrawerOpen: false,
            taxFormDetail: {},
            taxIsAdd: false,
            gybDrawerOpen: false,
            gybFormDetail: {},
            dialogType: { visible: false, desc: '', type: '' },
            deleteIds: [],
            taxPrimary: '',
            initialPageDataReady: false,
            taxSaveSubmitting: false,
        };
    }

    public async componentDidMount(): Promise<void> {
        await this.loadPageData();
    }

    /**
     * 将打印机列表转为多选下拉的 Option。
     * @param list - 打印机列表
     */
    private printersToOptions(list: KdsPrinter[]): Option[] {
        return (list || []).map((p) => ({
            value: String(p.print_device_id ?? ''),
            label: String(p.print_device_name ?? p.printer_name ?? ''),
        }));
    }

    /**
     * 将多选值还原为完整打印机对象数组。
     * @param opts - 选中项
     */
    private optionsToPrinters(opts: Option[]): KdsPrinter[] {
        const { kdsPrinterList } = this.state;
        const ids = new Set(opts.map((o) => Number(o.value)));
        return (kdsPrinterList || []).filter((p) => ids.has(Number(p.print_device_id)));
    }

    /**
     * 拉取国家、打印机、税务、GYB、相关 settings。
     */
    private loadPageData = async (): Promise<void> => {
        try {
        const rest = await apiManage_rbp.get_restaurant_info();
        const countryName = String(_.get(rest, 'country') || _.get(rest, 'name') || '').trim();
        const isSpain = countryName === 'Spain';

        const printerRes = await apiManage_rbp.getKdsPrintersLocation();
        if (!_.isArray(printerRes)) {
            GlobalNotification.error(i18n.t('dis_setting_page_get_printer_list_failed'));
        }

        const restRes = await apiManage_rbp.postKdsRestaurantInfoList(isSpain);
        if (restRes?.error || !_.isArray(restRes)) {
            GlobalNotification.error(i18n.t('dis_setting_page_get_tax_info_list_failed'));
        }

        const gybRes = isSpain ? [] : await apiManage_rbp.getGybDeviceInfo();
        if (!isSpain && gybRes?.error) {
            GlobalNotification.error(i18n.t('dis_setting_page_get_GYB_device_list_failed'));
        }

        const taxPrimaryRes = await apiManage_rbp.get_tax_primary();
        const taxPrimary = _.get(taxPrimaryRes, 'error') ? '' : String(_.get(taxPrimaryRes, 'amount', ''));

        const [res10, res33, res18, res43, res162, res90, res91, res92] = await Promise.all([
            apiManage_rbp.getSettingById(10),
            apiManage_rbp.getSettingById(33),
            apiManage_rbp.getSettingById(18),
            apiManage_rbp.getSettingById(43),
            apiManage_rbp.getSettingById(162),
            apiManage_rbp.getSettingById(90),
            apiManage_rbp.getSettingById(91),
            apiManage_rbp.getSettingById(92),
        ]);

        let printRetryNumber = this.state.printRetryNumber;
        let selectedKdsPrinterList: KdsPrinter[] = [];
        let testPrintParams = _.cloneDeep(this.state.testPrintParams);

        for (const val of [res90, res91, res92]) {
            const resId = _.get(val, '[0].id');
            if (resId === 90) {
                const n = Number(_.get(val, '[0].value', 0));
                if (n > 0) {
                    printRetryNumber = n;
                }
            } else if (resId === 91) {
                const savePrinters = _.get(val, '[0].value');
                if (Array.isArray(savePrinters) && savePrinters.length > 0) {
                    selectedKdsPrinterList = savePrinters;
                }
            } else if (resId === 92) {
                const testPrinterParams = _.get(val, '[0].value');
                if (Array.isArray(testPrinterParams) && testPrinterParams.length > 0) {
                    testPrintParams = testPrinterParams;
                }
            }
        }

        const settingValues = [res10, res33, res18, res43, res162];
        const invoiceRows = buildInitialInvoiceRows().map((row) => {
            const next = { ...row } as DisInvoiceRow;
            for (const res of settingValues) {
                const resId = _.get(res, '[0].id');
                if (resId !== row.key) {
                    continue;
                }
                const rawVal = _.get(res, '[0].value');
                if (row.key === 43) {
                    (next as Extract<DisInvoiceRow, { key: 43 }>).value = Number(rawVal ?? 0);
                } else if (row.key === 162) {
                    (next as Extract<DisInvoiceRow, { key: 162 }>).value = Boolean(Number(rawVal ?? 0));
                } else {
                    (next as Extract<DisInvoiceRow, { key: 10 | 33 | 18 }>).value =
                        rawVal === undefined || rawVal === null ? '' : String(rawVal);
                }
                break;
            }
            return next;
        });

        this.setState({
            invoiceRows,
            isSpain,
            countryName,
            kdsPrinterList: _.isArray(printerRes) ? printerRes : [],
            selectedKdsPrinterList,
            testPrintParams,
            printRetryNumber,
            kdsRestInfoList: restRes?.error || !_.isArray(restRes) ? [] : restRes,
            gybInvoiceInfoList: isSpain || gybRes?.error ? [] : gybRes,
            taxPrimary,
        });
        } finally {
            this.setState({ initialPageDataReady: true });
        }
    };

    /**
     * 根据行 id 生成 PATCH 参数（与 RBP2 `setParamById` 默认分支一致）。
     * @param row - 当前行
     * @returns PATCH body
     */
    private paramForRow(row: DisInvoiceRow): { stringValue: string } {
        if (row.type === 'switch') {
            return { stringValue: String(Number(!!row.value)) };
        }
        if (row.type === 'radio') {
            return { stringValue: String(row.value) };
        }
        return { stringValue: String(row.value ?? '') };
    }

    /**
     * 持久化：发票基础项 + 重试次数 + 打印机多选 + 测试参数。
     */
    private persistAll = async (): Promise<void> => {
        const { invoiceRows, printRetryNumber, selectedKdsPrinterList, testPrintParams } = this.state;
        setLoading(true);
        const tasks: Promise<any>[] = invoiceRows.map((row) => apiManage_rbp.patchSettingById(row.key, this.paramForRow(row)));
        tasks.push(apiManage_rbp.patchSettingById(90, { stringValue: String(printRetryNumber) }));
        tasks.push(apiManage_rbp.patchSettingByIdByArr(91, selectedKdsPrinterList));
        tasks.push(apiManage_rbp.patchSettingByIdByArr(92, testPrintParams));
        const results = await Promise.all(tasks);
        const failed = results.some((res) => _.get(res, 'error'));
        if (failed) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        }
        LSM.set({ path: LOCAL_STORAGE_KEYS.DIS_SETTING_PRINT_RETRY_NUM, value: printRetryNumber });
        setLoading(false);
    };

    /**
     * 点击保存。
     */
    private onClickSave = async (): Promise<void> => {
        if (this.saveLocked || this.state.isSaving || this.state.isSpain) {
            return;
        }
        this.saveLocked = true;
        this.setState({ isSaving: true });
        try {
            await this.persistAll();
        } finally {
            this.saveLocked = false;
            this.setState({ isSaving: false });
        }
    };

    /**
     * 修改发票行值。
     * @param key - 设置 id
     * @param val - 新值
     */
    private onChangeRow = (key: DisInvoiceRow['key'], val: string | number | boolean): void => {
        const invoiceRows = this.state.invoiceRows.map((row) => {
            if (row.key !== key) {
                return row;
            }
            if (row.type === 'radio') {
                return { ...row, value: Number(val) } as DisInvoiceRow;
            }
            if (row.type === 'switch') {
                return { ...row, value: Boolean(val) } as DisInvoiceRow;
            }
            return { ...row, value: String(val) } as DisInvoiceRow;
        });
        this.setState({ invoiceRows });
    };

    /**
     * 重试次数输入变更（Figma 为数字框；仍限制 3–10，与 RBP2 语义一致）。
     */
    private onPrintRetryInput = (raw: string): void => {
        if (this.state.isSpain) {
            return;
        }
        if (raw.trim() === '') {
            return;
        }
        const n = Number(raw);
        if (Number.isNaN(n)) {
            return;
        }
        this.setState({ printRetryNumber: Math.min(10, Math.max(3, Math.floor(n))) });
    };

    /**
     * 校验并保存税务信息（新增或编辑）。
     * @param data - 表单数据
     */
    private onSaveTax = async (data: DisRestaurantTaxFormValues): Promise<void> => {
        const checked = this.checkTaxInput(data);
        if (!checked) {
            return;
        }
        const { isSpain, taxIsAdd, taxPrimary } = this.state;
        if (isSpain) {
            return;
        }
        this.setState({ taxSaveSubmitting: true });
        setLoading(true);
        try {
            if (taxIsAdd) {
                const params = {
                    AliquotaIVA: taxPrimary || '',
                    Divisa: 'EUR',
                    EsigibilitaIVA: 'I',
                    IdPaese: 'IT',
                    Nazione: 'IT',
                    RegimeFiscale: 'RF01',
                    TipoDocumento: 'TD01',
                    ...checked,
                };
                const res = await apiManage_rbp.postCreateKdsRestaurantInfo(params, false);
                if (res?.error) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            } else {
                const saveParams = { id: [Number(checked.id)], key: [] as string[], value: [] as string[] };
                for (const key of Object.keys(checked)) {
                    if (key === 'id') {
                        continue;
                    }
                    saveParams.key.push(key);
                    saveParams.value.push(`'${(checked as any)[key] ?? ''}'`);
                }
                const res = await apiManage_rbp.patchKdsRestaurantInfo(saveParams, false);
                if (res?.error) {
                    GlobalNotification.error(i18n.t('global_text_operate_failure'));
                    return;
                }
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            this.setState({ taxDrawerOpen: false, taxFormDetail: {} }, async () => {
                await this.loadPageData();
            });
        } finally {
            setLoading(false);
            this.setState({ taxSaveSubmitting: false });
        }
    };

    /**
     * 校验税务表单（与 RBP2 `checkInput` 对齐的核心规则）。
     * @param inputItem - 原始表单
     * @returns 清洗后的对象；失败返回 null
     */
    private checkTaxInput(inputItem: DisRestaurantTaxFormValues): DisRestaurantTaxFormValues | null {
        const data: any = { ...inputItem };
        const { countryName } = this.state;
        try {
            if (countryName === 'Spain') {
                if (!_.get(data, 'CodiceFiscale', '')) {
                    throw new Error(i18n.t('dis_setting_page_type_entire_info'));
                }
                const regex = BASE_CHECK_TYPE.fiskalyNif.regular;
                if (!regex.test(String(data.CodiceFiscale))) {
                    throw new Error(i18n.t('general_text_spain_vat_number_invalid_format_text'));
                }
            } else if ((countryName !== 'Honduras' && !data.CodiceFiscale) || !data.IdCodice || !data.CAP || !data.Provincia) {
                throw new Error(i18n.t('dis_setting_page_type_entire_info'));
            }
            if (countryName === 'Italy') {
                let regex = /^([A-Za-z0-9]{16,16}|[0-9]{11,11})$/gi;
                if (!regex.test(String(data.CodiceFiscale))) {
                    throw new Error(i18n.t('dis_setting_page_codice_fiscale_must_num_alphbat'));
                }
                regex = /^[0-9]{11,11}$/gi;
                if (!regex.test(String(data.IdCodice))) {
                    throw new Error(i18n.t('dis_setting_page_id_codice_must_num'));
                }
                regex = /^[0-9]{5,5}$/gi;
                if (!regex.test(String(data.CAP))) {
                    throw new Error(i18n.t('dis_setting_page_cap_must_num'));
                }
                regex = /^[A-Z]{2,2}$/gi;
                if (!regex.test(String(data.Provincia))) {
                    throw new Error(i18n.t('dis_setting_page_province_must_alphba_length_two'));
                }
            }
        } catch (e: any) {
            GlobalNotification.error(e?.message || i18n.t('global_text_operate_failure'));
            return null;
        }
        data.Comune = String(data?.Comune ?? '').toUpperCase();
        data.CodiceFiscale = String(data?.CodiceFiscale ?? '').toUpperCase();
        data.Provincia = String(data?.Provincia ?? '').toUpperCase();
        return data;
    }

    /**
     * 删除单条税务信息（确认后）。
     */
    private onDeleteRestDevice = async (): Promise<void> => {
        const id = Number(_.get(this.state.taxFormDetail, 'id'));
        if (!id) {
            return;
        }
        const isSpain = this.state.isSpain;
        setLoading(true);
        const res = await apiManage_rbp.deleteKdsRestaurantInfo(id, isSpain);
        if (res?.error) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        }
        this.setState({ taxFormDetail: {}, dialogType: { visible: false, desc: '', type: '' } }, async () => {
            await this.loadPageData();
        });
        setLoading(false);
    };

    /**
     * 批量删除税务信息。
     */
    private onDeleteRestDeviceByIds = async (): Promise<void> => {
        const { deleteIds, isSpain } = this.state;
        if (deleteIds.length === 0) {
            return;
        }
        setLoading(true);
        const reqs = deleteIds.map((item) => apiManage_rbp.deleteKdsRestaurantInfo(item, isSpain));
        const values = await Promise.all(reqs);
        const error = values.some((item) => item?.error);
        if (!error) {
            GlobalNotification.success(i18n.t('global_text_operate_success'));
        } else {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        }
        this.setState({ deleteIds: [], dialogType: { visible: false, desc: '', type: '' } }, async () => {
            await this.loadPageData();
        });
        setLoading(false);
    };

    /**
     * 同步 GYB 设备。
     */
    private onSyncGYB = async (): Promise<void> => {
        const syncRes = await apiManage_rbp.patchGybSyncDevices();
        if (syncRes?.error) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
            return;
        }
        const gybRes = await apiManage_rbp.getGybDeviceInfo();
        this.setState({ gybInvoiceInfoList: gybRes?.error ? [] : gybRes });
        GlobalNotification.success(i18n.t('global_text_operate_success'));
    };

    /**
     * 设置或取消默认 GYB 设备。
     */
    private onSetDefaultGYB = async (id: number, isDefault: number): Promise<void> => {
        setLoading(true);
        let res: any;
        if (isDefault === 1) {
            res = await apiManage_rbp.deleteSetGybDefaultDevice(id);
        } else {
            res = await apiManage_rbp.patchSetGybDefaultDevice(id);
        }
        if (res?.error) {
            GlobalNotification.error(i18n.t('global_text_operate_failure'));
        } else {
            GlobalNotification.success(i18n.t('dis_setting_page_set_succeed'));
            await this.loadPageData();
        }
        setLoading(false);
    };

    /**
     * 确认弹窗提交。
     */
    private handleConfirmDialog = async (): Promise<void> => {
        const { type } = this.state.dialogType;
        if (type === 'sync') {
            await this.onSyncGYB();
        } else if (type === 'delete-rest') {
            await this.onDeleteRestDevice();
        } else if (type === 'delete-rest-ids') {
            await this.onDeleteRestDeviceByIds();
        }
    };

    /**
     * 操作列表头：不展示可见文字（对齐设计稿），保留 `th` 结构。
     */
    private static renderBlankActionHead(columnMeta: MUIDataTableCustomHeadRenderer): React.ReactNode {
        return <th key={columnMeta.name} className="MuiTableCell-root MuiTableCell-head" scope="col" />;
    }

    /**
     * 税务表格列定义。
     */
    private buildTaxColumns(): MUIDataTableColumnDef[] {
        const { countryName, isSpain, kdsRestInfoList } = this.state;
        const pivaObj: Record<string, string> = { Spain: 'CIF', Honduras: 'RTN' };
        const idCodiceLabel = pivaObj[countryName] || 'P.IVA';
        const codiceLabel = isSpain ? 'NIF' : 'Codice Fiscale';
        return [
            { name: 'id', label: 'ID', options: { sort: false } },
            { name: 'Denominazione', label: 'Denominazione', options: { sort: false } },
            { name: isSpain ? 'cif' : 'IdCodice', label: idCodiceLabel, options: { sort: false } },
            { name: isSpain ? 'nif' : 'CodiceFiscale', label: codiceLabel, options: { sort: false } },
            {
                name: '',
                label: '',
                options: {
                    sort: false,
                    download: false,
                    searchable: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const row = kdsRestInfoList[dataIndex];
                        return (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <IconButton
                                    color="primary"
                                    aria-label={i18n.t('google_text_detail')}
                                    onClick={() => {
                                        const current = { ...row };
                                        if (isSpain) {
                                            current.CodiceFiscale = _.get(row, 'nif', '') as string;
                                            current.IdCodice = _.get(row, 'cif', '') as string;
                                        }
                                        this.setState({
                                            taxDrawerOpen: true,
                                            taxFormDetail: current,
                                            taxIsAdd: false,
                                        });
                                    }}
                                    size="small"
                                >
                                    <Visibility />
                                </IconButton>
                                {isSpain ? null : (
                                    <IconButton
                                        aria-label={i18n.t('google_text_public_button_delete')}
                                        onClick={() => {
                                            this.setState(
                                                {
                                                    taxFormDetail: row,
                                                    dialogType: {
                                                        visible: true,
                                                        desc: i18n.t('dis_setting_page_confirm_dangerous_operation'),
                                                        type: 'delete-rest',
                                                    },
                                                },
                                                () => undefined,
                                            );
                                        }}
                                        size="small"
                                        /* 语义危险色，与 shell2 其它删除图标一致（勿用 theme.error 以免与设计 token 偏差） */
                                        sx={{ color: 'var(--func-error)' }}
                                    >
                                        <DeleteForeverIcon width={24} height={24} aria-hidden />
                                    </IconButton>
                                )}
                            </Box>
                        );
                    },
                },
            },
        ];
    }

    /**
     * GYB 表格列定义。
     */
    private buildGybColumns(): MUIDataTableColumnDef[] {
        const { gybInvoiceInfoList } = this.state;
        return [
            {
                name: 'is_default',
                label: i18n.t('dis_setting_page_default_device'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const row = gybInvoiceInfoList[dataIndex];
                        return (
                            <Checkbox
                                color="primary"
                                checked={Number(row?.is_default) === 1}
                                onChange={async () => {
                                    await this.onSetDefaultGYB(Number(row.id), Number(row.is_default));
                                }}
                            />
                        );
                    },
                },
            },
            { name: 'id', label: i18n.t('google_text_id'), options: { sort: false } },
            { name: 'location_id', label: i18n.t('dis_setting_page_signIn_id'), options: { sort: false } },
            { name: 'operator', label: i18n.t('dis_setting_page_operator'), options: { sort: false } },
            { name: 'device_code', label: i18n.t('dis_setting_page_device_code'), options: { sort: false } },
            { name: 'license_code', label: i18n.t('dis_setting_page_GYB_lecense_code'), options: { sort: false } },
            {
                name: '',
                label: '',
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const row = gybInvoiceInfoList[dataIndex];
                        return (
                            <IconButton
                                color="primary"
                                aria-label={i18n.t('google_text_detail')}
                                onClick={() => {
                                    this.setState({
                                        gybDrawerOpen: true,
                                        gybFormDetail: {
                                            ...row,
                                            valore_significato: '2C Solution',
                                        },
                                    });
                                }}
                                size="small"
                            >
                                <Visibility />
                            </IconButton>
                        );
                    },
                },
            },
        ];
    }

    public render(): React.ReactNode {
        if (!this.state.initialPageDataReady) {
            return <DisSettingPageSkeleton />;
        }
        const {
            isSaving,
            invoiceRows,
            isSpain,
            kdsPrinterList,
            selectedKdsPrinterList,
            testPrintParams,
            printRetryNumber,
            kdsRestInfoList,
            gybInvoiceInfoList,
            taxDrawerOpen,
            taxFormDetail,
            taxIsAdd,
            taxSaveSubmitting,
            gybDrawerOpen,
            gybFormDetail,
            dialogType,
        } = this.state;
        const row43 = invoiceRows.find((r): r is Extract<DisInvoiceRow, { key: 43 }> => r.key === 43);
        /** 文本/数字行共用分支，勿用 `Extract<..., { key: 33 }>`（在 TS 中会推断为 never） */
        const row10 = invoiceRows.find((r) => r.key === 10);
        const row33 = invoiceRows.find((r) => r.key === 33);
        const row18 = invoiceRows.find((r) => r.key === 18);
        const row162 = invoiceRows.find((r): r is Extract<DisInvoiceRow, { key: 162 }> => r.key === 162);
        const selectedOptions = this.printersToOptions(
            selectedKdsPrinterList.length ? selectedKdsPrinterList : [],
        );
        const printerOptions = this.printersToOptions(kdsPrinterList);

        return (
            <Box className="dis-setting-page">
                <Box className="dis-setting-page__surface">
                    <Box className="dis-setting-page__header">
                        <Typography variant="h4" color="primary" className="dis-setting-page__page-title">
                            {i18n.t('dis_setting_page_heading')}
                        </Typography>
                        <SubFunctionProjectSettingSaveButton
                            disabled={isSpain}
                            loading={isSaving}
                            onClick={this.onClickSave}
                        />
                    </Box>

                    {isSpain ? (
                        <Typography variant="body2" color="text.secondary" className="dis-setting-page__spain-hint">
                            {i18n.t('dis_setting_page_spain_readonly_hint')}
                        </Typography>
                    ) : null}

                    {/* 发票打印位置（Figma 1901:24516：整行 gap 16、底对齐；无「测试打印」按钮） */}
                    <Box className="dis-setting-page__card dis-setting-page__card--section">
                        <Typography variant="subtitle1" color="primary" className="dis-setting-page__section-title">
                            {i18n.t('dis_setting_page_print_position_title')}
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            alignItems="flex-end"
                            className="dis-setting-page__print-row dis-setting-page__gray-inputs"
                        >
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                    {i18n.t('dis_setting_page_printer_select_label')}
                                </Typography>
                                <CommonInput
                                    inputType="SELECT_MUL"
                                    size="small"
                                    fullWidth
                                    disabled={isSpain}
                                    label=""
                                    multipleProps={{
                                        options: printerOptions,
                                        value: selectedOptions,
                                        onChange: (opts: Option[]) => {
                                            this.setState({ selectedKdsPrinterList: this.optionsToPrinters(opts) });
                                        },
                                    }}
                                />
                            </Box>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-end" sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CommonInput
                                        inputType="SELECT"
                                        size="small"
                                        fullWidth
                                        disabled={isSpain}
                                        label={i18n.t('dis_setting_page_printer_type_label')}
                                        items={PRINTER_TYPE_ITEMS}
                                        value={testPrintParams[0].type}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const v = e.target.value;
                                            this.setState({
                                                testPrintParams: this.state.testPrintParams.map((p) => ({ ...p, type: v })),
                                            });
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CommonInput
                                        inputType="SELECT"
                                        size="small"
                                        fullWidth
                                        disabled={isSpain}
                                        label={i18n.t('dis_setting_page_printer_protocol_label')}
                                        items={PRINTER_AGREE_ITEMS}
                                        value={testPrintParams[0].agree}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const v = e.target.value;
                                            this.setState({
                                                testPrintParams: this.state.testPrintParams.map((p) => ({ ...p, agree: v })),
                                            });
                                        }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <CommonInput
                                        inputType="INPUT"
                                        size="small"
                                        fullWidth
                                        disabled={isSpain}
                                        label={i18n.t('dis_setting_page_printer_port_label')}
                                        value={testPrintParams[0].port}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            const v = e.target.value;
                                            this.setState({
                                                testPrintParams: this.state.testPrintParams.map((p) => ({ ...p, port: v })),
                                            });
                                        }}
                                    />
                                </Box>
                            </Stack>
                        </Stack>
                    </Box>

                    {/* 发票设置：2×3 成对网格（处理方式↔初始数值 等）；堆叠行仍为「上标题下控件」 */}
                    <Box className="dis-setting-page__card dis-setting-page__card--section dis-setting-page__invoice-card">
                        <Box className="dis-setting-page__invoice-title-row">
                            <Typography variant="subtitle1" color="primary">
                                {i18n.t('dis_setting_page_invoice_settings_title')}
                            </Typography>
                            <Button
                                variant="text"
                                color="primary"
                                disabled={isSpain}
                                onClick={() => this.setState({ printRetryNumber: 3 })}
                            >
                                {i18n.t('dis_setting_page_reset_retry')}
                            </Button>
                        </Box>
                        <Box className="dis-setting-page__invoice-body dis-setting-page__gray-inputs">
                            {/**
                             * 发票设置：2×3 网格，同一行左右成对顶对齐——处理方式↔初始数值、自动生成 XML↔XML 路径、后缀↔重试次数
                             */}
                            <Box className="dis-setting-page__invoice-paired-grid">
                                {row43 ? (
                                    <Box className="dis-setting-page__invoice-pair-cell">
                                        <Box className="dis-setting-page__invoice-field-row dis-setting-page__invoice-field-row--radios dis-setting-page__invoice-field-row--stacked">
                                            <Stack spacing={0.5} className="dis-setting-page__invoice-field-label">
                                                <Typography
                                                    id="dis-invoice-method-label"
                                                    variant="subtitle2"
                                                    color="text.primary"
                                                    component="span"
                                                >
                                                    {i18n.t(row43.labelKey)}
                                                </Typography>
                                                <SubFunctionAuxiliaryHint
                                                    enabled={!!this.context?.auxiliaryModeEnabled}
                                                    i18nKey="figma_aux_dis_invoice_processing_method"
                                                />
                                            </Stack>
                                            <Box className="dis-setting-page__invoice-field-control">
                                                <FormControl component="div" disabled={isSpain} fullWidth>
                                                    <RadioGroup
                                                        row
                                                        aria-labelledby="dis-invoice-method-label"
                                                        name="dis-invoice-processing-method"
                                                        value={row43.value}
                                                        onChange={(_, v) => this.onChangeRow(43, Number(v))}
                                                        className="dis-setting-page__invoice-radio-group"
                                                    >
                                                        {row43.options.map((opt) => (
                                                            <FormControlLabel
                                                                key={opt.value}
                                                                value={opt.value}
                                                                control={<Radio color="primary" />}
                                                                label={
                                                                    <Typography variant="body2">
                                                                        {i18n.t(opt.labelKey)}
                                                                    </Typography>
                                                                }
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                </FormControl>
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : null}
                                {row33 ? (
                                    <Box className="dis-setting-page__invoice-pair-cell">
                                        <Box className="dis-setting-page__invoice-field-row dis-setting-page__invoice-field-row--stacked">
                                            <Stack spacing={0.5} className="dis-setting-page__invoice-field-label">
                                                <Typography variant="subtitle2" color="text.primary">
                                                    {i18n.t(row33.labelKey)}
                                                </Typography>
                                            </Stack>
                                            <Box className="dis-setting-page__invoice-field-control">
                                                <CommonInput
                                                    inputType="INPUT"
                                                    type="number"
                                                    size="small"
                                                    fullWidth
                                                    disabled={isSpain}
                                                    label=""
                                                    value={String(row33.value)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        this.onChangeRow(33, e.target.value)
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : null}
                                {row162 ? (
                                    <Box className="dis-setting-page__invoice-pair-cell">
                                        <Box className="dis-setting-page__invoice-field-row">
                                            <Stack spacing={0.5} className="dis-setting-page__invoice-field-label">
                                                <Typography variant="subtitle2" color="text.primary">
                                                    {i18n.t(row162.labelKey)}
                                                </Typography>
                                                <SubFunctionAuxiliaryHint
                                                    enabled={!!this.context?.auxiliaryModeEnabled}
                                                    i18nKey="figma_aux_dis_xml_auto_generate"
                                                />
                                            </Stack>
                                            <Box className="dis-setting-page__invoice-field-control dis-setting-page__invoice-field-control--switch">
                                                <IOSSwitch
                                                    checked={!!row162.value}
                                                    disabled={isSpain}
                                                    onChange={() => this.onChangeRow(162, !row162.value)}
                                                    inputProps={{ 'aria-label': i18n.t(row162.labelKey) }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : null}
                                {row10 ? (
                                    <Box className="dis-setting-page__invoice-pair-cell">
                                        <Box className="dis-setting-page__invoice-field-row">
                                            <Stack spacing={0.5} className="dis-setting-page__invoice-field-label">
                                                <Typography variant="subtitle2" color="text.primary">
                                                    {i18n.t(row10.labelKey)}
                                                </Typography>
                                                <SubFunctionAuxiliaryHint
                                                    enabled={!!this.context?.auxiliaryModeEnabled}
                                                    i18nKey="figma_aux_dis_xml_output_path"
                                                />
                                            </Stack>
                                            <Box className="dis-setting-page__invoice-field-control">
                                                <CommonInput
                                                    inputType="INPUT"
                                                    size="small"
                                                    fullWidth
                                                    disabled={isSpain}
                                                    label=""
                                                    value={String(row10.value)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        this.onChangeRow(10, e.target.value)
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : null}
                                {row18 ? (
                                    <Box className="dis-setting-page__invoice-pair-cell">
                                        <Box className="dis-setting-page__invoice-field-row">
                                            <Stack spacing={0.5} className="dis-setting-page__invoice-field-label">
                                                <Typography variant="subtitle2" color="text.primary">
                                                    {i18n.t(row18.labelKey)}
                                                </Typography>
                                                <SubFunctionAuxiliaryHint
                                                    enabled={!!this.context?.auxiliaryModeEnabled}
                                                    i18nKey="figma_aux_dis_invoice_number_suffix"
                                                />
                                            </Stack>
                                            <Box className="dis-setting-page__invoice-field-control">
                                                <CommonInput
                                                    inputType="INPUT"
                                                    size="small"
                                                    fullWidth
                                                    disabled={isSpain}
                                                    label=""
                                                    value={String(row18.value)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        this.onChangeRow(18, e.target.value)
                                                    }
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : null}
                                <Box className="dis-setting-page__invoice-pair-cell">
                                    <Box className="dis-setting-page__invoice-field-row">
                                        <Stack spacing={0.5} className="dis-setting-page__invoice-field-label">
                                            <Typography variant="subtitle2" color="text.primary">
                                                {i18n.t('dis_setting_page_print_retry_number')}
                                            </Typography>
                                            <SubFunctionAuxiliaryHint
                                                enabled={!!this.context?.auxiliaryModeEnabled}
                                                i18nKey="figma_aux_dis_print_retry"
                                            />
                                        </Stack>
                                        <Box className="dis-setting-page__invoice-field-control">
                                            <CommonInput
                                                inputType="INPUT"
                                                type="number"
                                                size="small"
                                                fullWidth
                                                disabled={isSpain}
                                                label=""
                                                value={String(printRetryNumber)}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                    this.onPrintRetryInput(e.target.value)
                                                }
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* 餐厅税务信息 */}
                    <Box className="dis-setting-page__card dis-setting-page__card--section">
                        <Box className="dis-setting-page__table-head">
                            <Box>
                                <Typography variant="subtitle1" color="primary">
                                    {i18n.t('restaurant_tax_info')}
                                </Typography>
                                <SubFunctionAuxiliaryHint
                                    enabled={!!this.context?.auxiliaryModeEnabled}
                                    i18nKey="figma_aux_dis_restaurant_tax_section"
                                />
                            </Box>
                            {isSpain ? null : (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={taxDrawerOpen}
                                    onClick={() =>
                                        this.setState({
                                            taxDrawerOpen: true,
                                            taxFormDetail: {},
                                            taxIsAdd: true,
                                        })
                                    }
                                >
                                    {i18n.t('dis_setting_page_add_tax_info')}
                                </Button>
                            )}
                        </Box>
                        <Box className="dis-setting-page__datatable">
                            <Table
                                data={kdsRestInfoList}
                                columns={this.buildTaxColumns()}
                                search
                                pagination
                                rowsPerPage={20}
                                elevation={0}
                                otherOption={{
                                    selectableRowsHeader: !isSpain,
                                    selectableRowsHideCheckboxes: isSpain,
                                    selectToolbarPlacement: 'replace',
                                    onRowsDelete: (rowsDeleted: any) => {
                                        const deleteIds = rowsDeleted.data.map((item: { dataIndex: number }) =>
                                            Number(_.get(kdsRestInfoList, `[${item.dataIndex}].id`)),
                                        );
                                        this.setState(
                                            {
                                                deleteIds,
                                                dialogType: {
                                                    visible: true,
                                                    desc: i18n.t('dis_setting_page_confirm_dangerous_operation'),
                                                    type: 'delete-rest-ids',
                                                },
                                            },
                                            () => undefined,
                                        );
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    {/* GYB 设备（非西班牙） */}
                    {isSpain ? null : (
                        <Box className="dis-setting-page__card dis-setting-page__card--section">
                            <Box className="dis-setting-page__table-head">
                                <Box>
                                    <Typography variant="subtitle1" color="primary">
                                        {i18n.t('dis_setting_page_gyb_device')}
                                    </Typography>
                                    <SubFunctionAuxiliaryHint
                                        enabled={!!this.context?.auxiliaryModeEnabled}
                                        i18nKey="figma_aux_dis_gyb_device_section"
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() =>
                                        this.setState({
                                            dialogType: {
                                                visible: true,
                                                desc: i18n.t('dis_setting_page_whether_to_synchronize_gyb_device_data'),
                                                type: 'sync',
                                            },
                                        })
                                    }
                                >
                                    {i18n.t('dis_setting_page_synchronize')}
                                </Button>
                            </Box>
                            <Box className="dis-setting-page__datatable">
                                <Table
                                    data={gybInvoiceInfoList}
                                    columns={this.buildGybColumns()}
                                    search
                                    pagination
                                    rowsPerPage={20}
                                    elevation={0}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>

                <DisRestaurantTaxDrawer
                    open={taxDrawerOpen}
                    onClose={() =>
                        this.setState({ taxDrawerOpen: false, taxFormDetail: {}, taxSaveSubmitting: false })
                    }
                    country={this.state.countryName}
                    isSpain={isSpain}
                    isAdd={taxIsAdd}
                    detail={taxFormDetail}
                    saveSubmitting={taxSaveSubmitting}
                    onSave={(data) => this.onSaveTax(data)}
                />

                <DisGybDetailDrawer open={gybDrawerOpen} onClose={() => this.setState({ gybDrawerOpen: false })} detail={gybFormDetail} />

                <AlertDialog
                    open={dialogType.visible}
                    title={i18n.t('dis_setting_page_warning_tone_title')}
                    desc={dialogType.desc}
                    onCancel={() => this.setState({ dialogType: { visible: false, desc: '', type: '' } })}
                    onConfirm={async () => {
                        const cur = { ...dialogType };
                        this.setState({ dialogType: { ...cur, visible: false } }, async () => {
                            await this.handleConfirmDialog();
                        });
                    }}
                />
            </Box>
        );
    }
}

export default DisSettingPage;
